import { CSSProperties, FC, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { COLLAPSIBLE_SIDER_TRANSITION } from '../AnimatedNestedSiderLayout';

type SearchSiderPosition = 'left' | 'right';

interface AnimatedSearchSiderPanelProps {
    border?: boolean;
    children: ReactNode;
    open: boolean;
    padding?: CSSProperties['padding'];
    position?: SearchSiderPosition;
    siderLabel: string;
    style?: CSSProperties;
    width: CSSProperties['width'];
}

/**
 * Preserves Search's grid sizing while using the same width/opacity motion as
 * the primary platform sider. Keeping the children mounted also preserves
 * filter and detail-panel state between collapse and expand actions.
 */
const AnimatedSearchSiderPanel: FC<AnimatedSearchSiderPanelProps> = ({
    border = false,
    children,
    open,
    padding = 0,
    position = 'left',
    siderLabel,
    style,
    width,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const expandedWidth = typeof width === 'number' ? `${width}px` : width;
    const borderProperty = position === 'left' ? 'borderRight' : 'borderLeft';

    return (
        <aside
            aria-hidden={!open}
            aria-label={siderLabel}
            style={{
                ...style,
                [borderProperty]: border
                    ? open
                        ? `1px solid ${token.colorBorder}`
                        : '1px solid transparent'
                    : undefined,
                boxSizing: 'border-box',
                flex: open ? `0 0 ${expandedWidth}` : '0 0 0px',
                maxWidth: open ? expandedWidth : 0,
                minWidth: 0,
                opacity: open ? 1 : 0,
                overflow: 'hidden',
                padding: open ? padding : 0,
                pointerEvents: open ? undefined : 'none',
                transition: `${COLLAPSIBLE_SIDER_TRANSITION}, flex-basis 0.2s ease, max-width 0.2s ease, padding 0.2s ease, visibility 0s ${open ? '0s' : '0.2s'}`,
                visibility: open ? 'visible' : 'hidden',
                width: open ? expandedWidth : 0,
            }}
        >
            <div style={{ height: '100%', minHeight: 0, minWidth: 0, width: '100%' }}>{children}</div>
        </aside>
    );
};

export default AnimatedSearchSiderPanel;
